import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Card, Col, Row, Spinner } from "react-bootstrap";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import api from "../services/api";
import {
  extractArray,
  extractObject,
  normalizeNumericFields,
} from "../utils/apiData";
import {
  formatChartDate,
  formatCurrency,
  formatDateForApi,
  formatInteger,
} from "../utils/formatters";

const EmptyChart = ({ message }) => (
  <div className="empty-chart">{message}</div>
);

const Dashboard = ({ filters }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState({
    overview: {},
    dailyTrend: [],
    topProducts: [],
    shopRevenue: [],
  });

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const params = {
        start_date: formatDateForApi(filters.startDate),
        end_date: formatDateForApi(filters.endDate),
        shop_id: filters.shopId || undefined,
      };

      const [overviewRes, dailyRes, topProductsRes, shopRes] =
        await Promise.all([
          api.getOverview(params),
          api.getDailyTrend(params),
          api.getTopProducts({ ...params, limit: 10 }),
          api.getShopRevenue(params),
        ]);

      setData({
        overview: extractObject(overviewRes),
        dailyTrend: normalizeNumericFields(extractArray(dailyRes), [
          "daily_revenue",
          "invoice_count",
          "items_sold",
        ]),
        topProducts: normalizeNumericFields(extractArray(topProductsRes), [
          "total_quantity_sold",
          "total_revenue",
          "avg_price",
          "times_purchased",
        ]),
        shopRevenue: normalizeNumericFields(extractArray(shopRes), [
          "total_transactions",
          "total_revenue",
          "total_discounts",
          "total_items_sold",
        ]),
      });
    } catch (requestError) {
      setError(requestError.message || "Failed to fetch dashboard data.");
    } finally {
      setLoading(false);
    }
  }, [filters.endDate, filters.shopId, filters.startDate]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  if (loading) {
    return (
      <div className="content-loader">
        <Spinner animation="border" variant="danger" role="status" />
        <span>Loading dashboard data…</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="mt-3">
        <Alert.Heading>Dashboard could not load</Alert.Heading>
        <p className="mb-3">{error}</p>
        <Button variant="outline-danger" onClick={fetchDashboardData}>
          Retry
        </Button>
      </Alert>
    );
  }

  const overview = data.overview;

  return (
    <div>
      <div className="section-heading">
        <div>
          <p className="eyebrow">Performance overview</p>
          <h1>Sales Dashboard</h1>
        </div>
        <Button variant="outline-dark" onClick={fetchDashboardData}>
          Refresh Data
        </Button>
      </div>

      <Row className="mb-4 g-3">
        <Col md={3} sm={6}>
          <Card className="kpi-card h-100">
            <Card.Body>
              <div className="kpi-label">Total Revenue</div>
              <div className="kpi-value">{formatCurrency(overview.total_revenue)}</div>
              <div className="kpi-sub">
                {formatInteger(overview.total_invoices)} transactions
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6}>
          <Card className="kpi-card h-100">
            <Card.Body>
              <div className="kpi-label">Items Sold</div>
              <div className="kpi-value">{formatInteger(overview.total_items_sold)}</div>
              <div className="kpi-sub">
                {formatInteger(overview.unique_products)} unique products
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6}>
          <Card className="kpi-card h-100">
            <Card.Body>
              <div className="kpi-label">Average Order Value</div>
              <div className="kpi-value">
                {formatCurrency(overview.average_order_value)}
              </div>
              <div className="kpi-sub">
                Discounts: {formatCurrency(overview.total_discounts)}
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6}>
          <Card className="kpi-card h-100">
            <Card.Body>
              <div className="kpi-label">Unique Sellers</div>
              <div className="kpi-value">{formatInteger(overview.unique_sellers)}</div>
              <div className="kpi-sub">Active sellers</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4 g-3">
        <Col lg={8}>
          <Card className="h-100">
            <Card.Header>Daily Sales Trend</Card.Header>
            <Card.Body>
              {data.dailyTrend.length ? (
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={data.dailyTrend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(value) => formatChartDate(value)}
                    />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip
                      labelFormatter={(value) =>
                        formatChartDate(value, "MMM dd, yyyy")
                      }
                      formatter={(value, name) =>
                        name === "Revenue" ? formatCurrency(value) : value
                      }
                    />
                    <Legend />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="daily_revenue"
                      stroke="#c8102e"
                      name="Revenue"
                      strokeWidth={3}
                      dot={false}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="items_sold"
                      stroke="#202020"
                      name="Items Sold"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChart message="No daily trend data for this period." />
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col lg={4}>
          <Card className="h-100">
            <Card.Header>Top Products</Card.Header>
            <Card.Body>
              {data.topProducts.length ? (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={data.topProducts} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis
                      dataKey="name"
                      type="category"
                      width={90}
                      tick={{ fontSize: 11 }}
                    />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Bar dataKey="total_revenue" fill="#c8102e" name="Revenue" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChart message="No product data for this period." />
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3">
        <Col lg={12}>
          <Card>
            <Card.Header>Shop Performance</Card.Header>
            <Card.Body>
              {data.shopRevenue.length ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={data.shopRevenue}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="shop_name" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip
                      formatter={(value, name) =>
                        name === "Revenue" ? formatCurrency(value) : value
                      }
                    />
                    <Legend />
                    <Bar
                      yAxisId="left"
                      dataKey="total_revenue"
                      fill="#c8102e"
                      name="Revenue"
                    />
                    <Bar
                      yAxisId="right"
                      dataKey="total_transactions"
                      fill="#343a40"
                      name="Transactions"
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChart message="No shop performance data for this period." />
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
